import { useId, type ReactNode } from 'react'
import styles from './Field.module.css'

interface FieldProps {
  label: string
  /** Пояснение под полем. Скрывается, когда есть ошибка. */
  hint?: string
  error?: string
  required?: boolean
  /**
   * Поле получает готовые id и aria-атрибуты.
   *
   * Обёртка отдаёт их вниз функцией, а не просит вызывающего завести
   * свой id: без связки label и поля щелчок по подписи не ставит курсор,
   * а экранный диктор читает поле безымянным.
   */
  children: (props: {
    id: string
    'aria-invalid': boolean | undefined
    'aria-describedby': string | undefined
  }) => ReactNode
}

export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId()
  const messageId = `${id}-message`
  const hasMessage = Boolean(error || hint)

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': hasMessage ? messageId : undefined,
      })}

      {error ? (
        // role="alert" — чтобы диктор прочитал ошибку сразу, а не когда
        // человек до неё доберётся.
        <span className={styles.error} id={messageId} role="alert">
          {error}
        </span>
      ) : (
        hint && (
          <span className={styles.hint} id={messageId}>
            {hint}
          </span>
        )
      )}
    </div>
  )
}
